import { ConsentBlock, ConsentSpan, PATIENT_NAME_PLACEHOLDER, consentDocument } from "../consentDocument"
import { ConsentType, ShownConsents, useSignConsent } from "../hooks"
import { useTrackBookingScreenView, useBookingTracking } from "../tracking"
import { BackIcon, Box, ForwardIcon, Link, Typography } from "@carrotfertility/carotene-core"
import {
  Form,
  FormAlert,
  FormButton,
  FormCheckbox,
  FormTextField,
  useFormContext,
  useWatch
} from "@carrotfertility/carotene-core-x"
import React from "react"
import { BookingBreadcrumbs } from "../Breadcrumbs"

// Fixed page order; only the outstanding subset is shown, so the step count shrinks as consents are
// already-signed-and-valid (skipped upstream by the consent gate).
const CONSENT_ORDER: ConsentType[] = ["telehealth_informed_consent", "notice_of_privacy_practices", "hipaa"]
// Consents that do NOT gate Continue. HIPAA is shown annually and optionally signed (see the BFF gate).
const OPTIONAL_CONSENTS = new Set<ConsentType>(["hipaa"])
// Only telehealth informed consent + consent-to-treat is ratified by a typed-name signature; the rest
// are checkbox acknowledgements.
const SIGNATURE_CONSENT: ConsentType = "telehealth_informed_consent"

type StepFormValues = {
  // Present on the signature step only; gates the CTA client-side — never sent or stored.
  signature: string
  // Present on checkbox steps only.
  acknowledged: boolean
}

const EMPTY_STEP: StepFormValues = { signature: "", acknowledged: false }

// The field a page gates its Continue button on.
type ConsentInput = keyof StepFormValues

// Fill the member's legal name into the HIPAA "My name:" line (the only copy carrying the token).
function fillSpans(spans: ConsentSpan[], memberName: string): ConsentSpan[] {
  return spans.map((span) =>
    span.text.includes(PATIENT_NAME_PLACEHOLDER)
      ? { ...span, text: span.text.split(PATIENT_NAME_PLACEHOLDER).join(memberName) }
      : span
  )
}
function fillPatientName(blocks: ConsentBlock[], memberName: string): ConsentBlock[] {
  return blocks.map((block) =>
    block.type === "paragraph"
      ? { ...block, spans: fillSpans(block.spans, memberName) }
      : {
          ...block,
          items: block.items.map((item) => ({
            ...item,
            spans: fillSpans(item.spans, memberName),
            ...(item.blocks ? { blocks: fillPatientName(item.blocks, memberName) } : {})
          }))
        }
  )
}

type ConsentPageProps = {
  // The consents the member still needs to act on (from getConsentStatus), in CONSENT_ORDER. One page
  // each; each is reported (signed true/false) in a single signConsent on the final page.
  outstandingConsents: ConsentType[]
  // The member's legal name, filled into the HIPAA "My name:" line. May be empty if not yet loaded.
  memberName: string
  onContinue: () => void
}

// Literal strings (no react-intl): this package isn't in the intl extraction pipeline and the
// experience is English-only for the foreseeable future. All copy comes from consentDocument.
export function ConsentPage({ outstandingConsents, memberName, onContinue }: ConsentPageProps): JSX.Element | null {
  const signConsent = useSignConsent()
  const tracking = useBookingTracking()

  // Snapshot at mount: the final submit invalidates the consent-status query, which can report zero
  // outstanding while the machine is still here — recomputing would empty shownTypes and crash.
  const [shownTypes] = React.useState(() => {
    const outstanding = new Set(outstandingConsents)
    return CONSENT_ORDER.filter((type) => outstanding.has(type))
  })

  const [stepIndex, setStepIndex] = React.useState(0)
  // Captured values persist here so Back/forward restores them (the form remounts per step via key={type}),
  // and the final page submits them all in ONE signConsent — the BFF gates over the whole payload.
  const valuesByType = React.useRef<Partial<Record<ConsentType, StepFormValues>>>({})

  const type: ConsentType | undefined = shownTypes[stepIndex]
  const entry = type ? consentDocument.consents[type] : undefined
  const body = React.useMemo(() => (entry ? fillPatientName(entry.section.body, memberName) : []), [entry, memberName])
  useTrackBookingScreenView(type && entry ? { screen: "consent", consentStep: type } : null)

  // Nothing to sign — a consent-status refetch can empty the set between a NEEDS_CONSENT re-entry and
  // mount. Advance the flow rather than render an empty step (or crash on a missing entry).
  React.useEffect(() => {
    if (shownTypes.length === 0) {
      tracking.onEvent({ type: "consent_skipped" })
      onContinue()
    }
  }, [shownTypes.length, onContinue, tracking])
  if (type === undefined || entry === undefined) return null

  const optional = OPTIONAL_CONSENTS.has(type)
  const usesSignature = type === SIGNATURE_CONSENT
  const isLastStep = stepIndex === shownTypes.length - 1
  // The single input this page renders, and the field Continue gates on — unless the consent is
  // optional (HIPAA), which gates on nothing.
  const field: ConsentInput = usesSignature ? "signature" : "acknowledged"
  const requires: ConsentInput | null = optional ? null : field
  const savedValues = valuesByType.current[type] ?? EMPTY_STEP

  // A consent is "signed" if its captured field is satisfied. Continue-gating guarantees required consents
  // are satisfied before the final submit; HIPAA carries its actual checkbox state.
  const signedFor = (consentType: ConsentType): boolean => {
    const values = valuesByType.current[consentType]
    return consentType === SIGNATURE_CONSENT
      ? (values?.signature ?? "").trim().length > 0
      : Boolean(values?.acknowledged)
  }

  const handleStepSubmit = (values: StepFormValues) => {
    valuesByType.current[type] = values
    if (!isLastStep) {
      setStepIndex((index) => index + 1)
      return
    }
    const shown: ShownConsents = shownTypes.map((shownType) => ({ type: shownType, signed: signedFor(shownType) }))
    signConsent.mutate(shown, { onSuccess: onContinue })
  }

  const handleBack = (values: StepFormValues) => {
    valuesByType.current[type] = values
    // Clear a failed-submit error so it doesn't follow the member back onto a non-submit step.
    signConsent.reset()
    setStepIndex((index) => Math.max(0, index - 1))
  }

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(theme.tokens.spacing.lg),
        maxInlineSize: 640
      })}
    >
      <BookingBreadcrumbs />

      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}>
        <Typography variant="h3" component="h1" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {`${consentDocument.title} (${stepIndex + 1} of ${shownTypes.length})`}
        </Typography>
        <Typography>{consentDocument.intro}</Typography>
      </Box>

      <Form<StepFormValues> key={type} onSubmit={handleStepSubmit} defaultValues={savedValues}>
        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.md) })}
        >
          <Typography variant="h4" component="h2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            {entry.section.heading}
          </Typography>

          {/* Focusable + labelled so keyboard users can scroll and read the clipped legal text. */}
          <Box
            tabIndex={0}
            role="region"
            aria-label={entry.section.heading}
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(theme.tokens.spacing.sm),
              maxBlockSize: 320,
              overflowY: "auto",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.tokens.borderRadius.md,
              padding: theme.spacing(theme.tokens.spacing.md)
            })}
          >
            {body.map((block, index) => (
              <ConsentBlockView key={index} block={block} />
            ))}
          </Box>

          <Box sx={(theme) => ({ marginBlockStart: theme.spacing(theme.tokens.spacing.sm) })}>
            {/* `required` is the asterisk + aria only — not an RHF rule, so there's no error state. */}
            {usesSignature ? (
              <FormTextField name="signature" label={consentDocument.signature.label} required />
            ) : (
              <FormCheckbox name="acknowledged" label={entry.clause?.label ?? ""} required={!optional} />
            )}
          </Box>

          {signConsent.isError && (
            <FormAlert severity="error">Something went wrong saving your consent. Please try again.</FormAlert>
          )}

          <StepFooter
            requires={requires}
            pending={signConsent.isPending}
            showBack={stepIndex > 0}
            onBack={handleBack}
          />
        </Box>
      </Form>
    </Box>
  )
}

// Lives inside <Form> to watch field values: Continue stays disabled until the required input is captured
// (design: no error state). Both fields exist in defaultValues, so watching the one not rendered is harmless.
function StepFooter({
  requires,
  pending,
  showBack,
  onBack
}: {
  requires: ConsentInput | null
  pending: boolean
  showBack: boolean
  onBack: (values: StepFormValues) => void
}): JSX.Element {
  const { getValues } = useFormContext<StepFormValues>()
  const signature = useWatch({ name: "signature" }) as string | undefined
  const acknowledged = useWatch({ name: "acknowledged" }) as boolean | undefined
  // Each input's satisfied state; the page gates Continue on whichever one it `requires` (if any).
  const satisfied: Record<ConsentInput, boolean> = {
    signature: (signature ?? "").trim().length > 0,
    acknowledged: Boolean(acknowledged)
  }
  const unmet = requires !== null && !satisfied[requires]

  return (
    <Box sx={{ display: "flex", justifyContent: showBack ? "space-between" : "flex-end", alignItems: "center" }}>
      {showBack && (
        <FormButton
          type="button"
          variant="text"
          color="secondary"
          startIcon={<BackIcon />}
          disabled={pending}
          onClick={() => onBack(getValues())}
        >
          Back
        </FormButton>
      )}
      <FormButton
        type="submit"
        variant="contained"
        color="secondary"
        endIcon={<ForwardIcon />}
        disabled={pending || unmet}
      >
        Continue
      </FormButton>
    </Box>
  )
}

// Inline spans of one block: external links, bold emphasis, or plain text.
function renderSpans(spans: ConsentSpan[]): React.ReactNode {
  return spans.map((span, index) => {
    if (span.href) {
      return (
        <Link key={index} href={span.href} target="_blank" rel="noopener noreferrer" aria-label={span.ariaLabel}>
          {span.text}
        </Link>
      )
    }
    return span.bold ? (
      <Box key={index} component="span" sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
        {span.text}
      </Box>
    ) : (
      <React.Fragment key={index}>{span.text}</React.Fragment>
    )
  })
}

// One body block: a paragraph, or a semantic <ul> whose items may own nested blocks (an explanatory
// paragraph, a deeper list). Real <ul>/<li> means assistive tech announces the list, item count,
// boundaries, and parent/child relationships (WCAG 1.3.1). Native disc markers are kept (rather than
// list-style:none) because Safari/VoiceOver drops list semantics when the marker is removed.
function ConsentBlockView({ block }: { block: ConsentBlock }): JSX.Element {
  if (block.type === "paragraph") {
    return <Typography>{renderSpans(block.spans)}</Typography>
  }
  return (
    <Box
      component="ul"
      sx={(theme) => ({
        marginBlock: 0,
        paddingInlineStart: theme.spacing(theme.tokens.spacing.lg),
        listStyleType: "disc"
      })}
    >
      {block.items.map((item, index) => (
        <Typography
          component="li"
          key={index}
          sx={(theme) => ({ "&:not(:first-of-type)": { marginBlockStart: theme.spacing(theme.tokens.spacing.sm) } })}
        >
          {renderSpans(item.spans)}
          {item.blocks?.map((child, childIndex) => (
            <Box key={childIndex} sx={(theme) => ({ marginBlockStart: theme.spacing(theme.tokens.spacing.xs) })}>
              <ConsentBlockView block={child} />
            </Box>
          ))}
        </Typography>
      ))}
    </Box>
  )
}
